import { inject } from '@angular/core';
import { LogHelper } from '../../logging/log-helper';
import { DASHBOARD_CONFIG } from '../dashboard-grid.tokens';
import { DashboardWidgetRegistry } from '../widget/dashboard-widget-registry';
import { sanitizeSettings } from '../widget/dashboard-widget-settings';
import {
  DASHBOARD_LAYOUT_VERSION,
  DashboardLayout,
  DashboardWidgetInstance,
  DEFAULT_REFRESH_INTERVAL_SECONDS,
  GridRect,
} from './dashboard-layout';
import { uniqueId } from '../../model/utility';
import { findFreeSpot, GeometryRules, normalizeGeometry, readingOrder } from './dashboard-layout-engine';
import {
  DashboardLayoutConstraints,
  DashboardLayoutParseResult,
  geometryRulesOf,
  filterDefaultProblem,
  parseDashboardLayout,
  withSettings,
} from './dashboard-layout-parse';

/** One change to a layout, as the user makes it in Customize mode. */
export type DashboardLayoutEdit =
  /** Adds a widget of `type` at the first free spot */
  | { kind: 'add'; type: string }
  | { kind: 'remove'; id: string }
  /** Removes every widget of `type` */
  | { kind: 'removeType'; type: string }
  /**
   * Where the grid placed the visible widgets after a drag, resize or keyboard change; `keyboard` names the widget
   * the keyboard moved or resized
   */
  | {
      kind: 'arrange';
      placements: readonly { id: string; rect: GridRect }[];
      keyboard?: { kind: 'moveBy' | 'resizeBy'; id: string };
    }
  | { kind: 'settings'; id: string; settings: unknown }
  | { kind: 'filterDefault'; key: string; value: unknown }
  | { kind: 'autoRefresh'; enabled: boolean };

export type DashboardLayoutEditRejection =
  /** No such widget or widget type in the layout, the widget is hidden from this user, or Customize mode is off */
  | 'not-found'
  /** The type is unknown, not permitted or already at `maxInstances` */
  | 'not-allowed'
  /** The settings fail the widget type's schema */
  | 'invalid-settings'
  /** The name is not a filter of the dashboard */
  | 'unknown-filter'
  /** The value is not one of the filter's options */
  | 'invalid-filter-value';

export type DashboardLayoutEditResult =
  /** `widget`: the one added, changed or moved by keyboard, or the one removed */
  | { applied: true; layout: DashboardLayout; widget?: DashboardWidgetInstance }
  | { applied: false; reason: DashboardLayoutEditRejection };

/** Every rule a layout must keep. A layout from `parse` or `apply` is always valid for the current user. */
export class DashboardLayouts {
  private readonly geometry: GeometryRules;

  constructor(private readonly constraints: DashboardLayoutConstraints) {
    this.geometry = geometryRulesOf(constraints);
  }

  /** Turns untrusted JSON into a valid layout, dropping what is malformed or not allowed; never throws. */
  parse(raw: unknown): DashboardLayoutParseResult {
    try {
      return parseDashboardLayout(raw, this.constraints);
    } catch (error) {
      LogHelper.error('DashboardLayouts', 'Parsing a layout threw, which is a bug', error);
      return { layout: null, warnings: [`Layout could not be parsed: ${String(error)}`] };
    }
  }

  /**
   * A dashboard's built-in layout for this user: widgets keep their columns, widgets the user can't see go below the
   * others, and what they leave empty above is closed upward. An unusable layout gives an empty dashboard.
   */
  parseDefault(raw: unknown): { layout: DashboardLayout; warnings: string[]; unusable: boolean } {
    const { layout, warnings } = this.parse(raw);
    if (!layout) {
      return {
        layout: { version: DASHBOARD_LAYOUT_VERSION, widgets: [] },
        warnings: [...warnings, 'built-in layout is unusable; showing an empty dashboard'],
        unusable: true,
      };
    }

    return { layout, warnings, unusable: false };
  }

  /** Applies one edit to a valid layout; the result is valid too, or the edit is rejected and nothing changes. */
  apply(layout: DashboardLayout, edit: DashboardLayoutEdit): DashboardLayoutEditResult {
    switch (edit.kind) {
      case 'add':
        return this.add(layout, edit.type);
      case 'remove':
        return this.remove(layout, edit.id);
      case 'removeType':
        return this.removeType(layout, edit.type);
      case 'arrange':
        return this.arrange(layout, edit.placements, edit.keyboard?.id);
      case 'settings':
        return this.changeSettings(layout, edit.id, edit.settings);
      case 'filterDefault':
        return this.changeFilterDefault(layout, edit.key, edit.value);
      case 'autoRefresh':
        return applied({
          ...layout,
          refresh: {
            enabled: edit.enabled,
            intervalSeconds: layout.refresh?.intervalSeconds ?? DEFAULT_REFRESH_INTERVAL_SECONDS,
          },
        });
    }
  }

  /** The widgets this user sees, in reading order */
  visibleWidgets(layout: DashboardLayout): DashboardWidgetInstance[] {
    return readingOrder(layout.widgets.filter((widget) => this.geometry.isVisible(widget)));
  }

  private add(layout: DashboardLayout, type: string): DashboardLayoutEditResult {
    const definition = this.constraints.widgets.find(type);
    if (!definition || !this.constraints.widgets.canAdd(type, layout.widgets)) {
      return rejected('not-allowed');
    }

    const size = definition.size.default;
    const instance: DashboardWidgetInstance = {
      id: uniqueId(type, layout.widgets),
      type,
      ...findFreeSpot(layout.widgets, size, this.geometry),
      ...size,
    };
    if (definition.defaultSettings !== undefined) {
      instance.settings = structuredClone(definition.defaultSettings);
    }

    const widgets = normalizeGeometry([...layout.widgets, instance], this.geometry);
    return applied({ ...layout, widgets }, instance.id);
  }

  private remove(layout: DashboardLayout, id: string): DashboardLayoutEditResult {
    const widget = layout.widgets.find((candidate) => candidate.id === id);
    if (!widget || !this.geometry.isVisible(widget)) {
      return rejected('not-found');
    }

    const widgets = normalizeGeometry(
      layout.widgets.filter((candidate) => candidate !== widget),
      this.geometry,
    );
    return { applied: true, layout: { ...layout, widgets }, widget };
  }

  private removeType(layout: DashboardLayout, type: string): DashboardLayoutEditResult {
    if (!layout.widgets.some((widget) => widget.type === type)) {
      return rejected('not-found');
    }

    const widgets = normalizeGeometry(
      layout.widgets.filter((widget) => widget.type !== type),
      this.geometry,
    );
    return applied({ ...layout, widgets });
  }

  /** Takes the grid's placements for visible widgets, then normalizes; hidden widgets are re-stacked below */
  private arrange(
    layout: DashboardLayout,
    placements: readonly { id: string; rect: GridRect }[],
    keyboardId: string | undefined,
  ): DashboardLayoutEditResult {
    const rects = new Map(placements.map(({ id, rect }) => [id, rect]));
    const moved = layout.widgets.map((widget) => {
      const rect = this.geometry.isVisible(widget) ? rects.get(widget.id) : undefined;
      return rect ? { ...widget, x: rect.x, y: rect.y, w: rect.w, h: rect.h } : widget;
    });
    return applied({ ...layout, widgets: normalizeGeometry(moved, this.geometry) }, keyboardId);
  }

  private changeSettings(layout: DashboardLayout, id: string, settings: unknown): DashboardLayoutEditResult {
    const widget = layout.widgets.find((candidate) => candidate.id === id);
    const definition = widget && this.constraints.widgets.find(widget.type);
    if (!widget || !definition || !this.geometry.isVisible(widget)) {
      return rejected('not-found');
    }

    const result = sanitizeSettings(definition, settings);
    if (result.invalid) {
      return rejected('invalid-settings');
    }

    const widgets = layout.widgets.map((candidate) =>
      candidate === widget ? withSettings(candidate, result.settings) : candidate,
    );
    return applied({ ...layout, widgets }, id);
  }

  private changeFilterDefault(layout: DashboardLayout, key: string, value: unknown): DashboardLayoutEditResult {
    const problem = filterDefaultProblem(key, value, this.constraints.state);
    if (problem) {
      return rejected(problem === 'not-option' ? 'invalid-filter-value' : 'unknown-filter');
    }

    return applied({ ...layout, filterDefaults: { ...layout.filterDefaults, [key]: value } });
  }
}

export function dashboardLayoutsFactory(): DashboardLayouts {
  const config = inject(DASHBOARD_CONFIG);
  return new DashboardLayouts({
    widgets: inject(DashboardWidgetRegistry),
    columns: config.grid.columns,
    state: config.state,
  });
}

function applied(layout: DashboardLayout, widgetId?: string): DashboardLayoutEditResult {
  const widget = widgetId === undefined ? undefined : layout.widgets.find((candidate) => candidate.id === widgetId);
  return { applied: true, layout, widget };
}

function rejected(reason: DashboardLayoutEditRejection): DashboardLayoutEditResult {
  return { applied: false, reason };
}
